import axios from 'axios';
export const ACCESS_TOKEN_KEY = 'access-token';
/**
 * Adds bearer token headers for api calls that use the standard aws API Gateway Authorizer
 * and handles refreshing the bearer token using the  logic provided as the dependency
 * @param logic {AUTH.Logic} config for the authn+authz
 * @param options.header {string} optional param for where to set the Bearer token
 * @param options.token {string} previous token value for initial use
 */
export const bearer = (logic, options) => {
    const tokenheader = options?.header || 'x-token';
    const authz = {
        token: undefined,
        mutex: undefined, //synchronization tool to avoid race conditions with in-flight requests
    };
    const initialToken = (async () => {
        try {
            authz.token = options?.token || await logic.authz.getAccessToken();
        }
        catch (error) {
            if (process.env.GMK_AXIOS_DEBUG) {
                console.debug('[@growmark/axios]:', error);
            }
        }
    })();
    return {
        request: (client) => async (config) => {
            if (!authz.token) {
                await initialToken;
            }
            const headers = config.headers || { ['x-nothing']: 'no-original-headers' };
            const xtoken = authz.token ? { [tokenheader]: authz.token } : {};
            return {
                ...config, headers: {
                    ...headers, ...xtoken
                }
            };
        },
        response: client => async (response) => response,
        error: (client) => async (error) => {
            //do checking up-front for all the detail we need
            const { config, response } = error;
            if (!config || !response) {
                throw error;
            }
            if (response.status !== 401 && response.status !== 403) {
                throw error;
            }
            //if (limit > 0 && await logic.authn.isAuthenticated()) { limit = 0; throw error; }
            //wait 2 seconds if we had 2 unsuccessful attempts
            const gmk_axios_request_limit = (config.gmk_axios_request_limit || 0) + 1;
            if (gmk_axios_request_limit > 5) {
                throw error;
            }
            if (gmk_axios_request_limit > 2) {
                await new Promise(resolve => setTimeout(resolve, gmk_axios_request_limit * 1000));
            }
            //get previous token for comparison
            const previousToken = config?.headers?.[tokenheader];
            //if a call is already in flight lets use that one otherwise create one
            authz.mutex = (authz.mutex || (async () => {
                //=============IMPORTANT token fetching logic ===================
                try {
                    if (process.env.GMK_AXIOS_DEBUG) {
                        console.debug('[@growmark/axios]:', 'initial token attempt');
                    }
                    if (!(await logic.authn.isAuthenticated())) {
                        throw error;
                    }
                    authz.token = await logic.authz.getAccessToken();
                    if (process.env.GMK_AXIOS_DEBUG) {
                        console.debug('[@growmark/axios]: token updated');
                    }
                }
                catch (problem) {
                    if (process.env.GMK_AXIOS_DEBUG) {
                        console.debug('[@growmark/axios]: issue with token update', problem);
                    }
                    if (!logic) {
                        throw problem;
                    }
                    const status = axios.isAxiosError(problem) && problem?.response?.status;
                    switch (status) {
                        default: throw problem;
                        case 403:
                        case 401:
                            {
                                if (process.env.GMK_AXIOS_DEBUG) {
                                    console.debug('[@growmark/axios]: clearing and trying again');
                                }
                                //remove previous access token because it is obviously bad
                                authz.token = undefined;
                                //check if we are authenticated and if not then signin
                                await logic.authn.signin();
                                //assign and store data
                                authz.token = await logic.authz.getAccessToken();
                                if (process.env.GMK_AXIOS_DEBUG) {
                                    console.debug('[@growmark/axios]: token updated');
                                }
                            }
                            break;
                    }
                }
                finally {
                    authz.mutex = undefined;
                }
                //=========================================================
            })());
            await authz.mutex; //wait for synchronized process to finish
            //token assignment
            if (!authz.token) {
                throw error;
            } //no token so... umm it will be unauthed... duh
            if (authz.token == previousToken) {
                throw error;
            } //same token so nothing is going to change
            //make call again but without the agents
            const { httpAgent, httpsAgent, url, ...otherStuff } = config || {};
            const headers = config.headers || {};
            const xtoken = authz.token ? { [tokenheader]: authz.token } : {};
            // let error here bubble out of the async function and "reject" it
            const again = {
                url, ...otherStuff,
                headers: { ...headers, ...xtoken },
                gmk_axios_request_limit //not part of axios request but used to ensure infinate loop doesn't happen
            };
            return await client(again);
        }
    };
};
//# sourceMappingURL=authorization.bearer.js.map